// Raíz: caché de consultas → pestañas → sesión (que al cambiar de empresa reinicia las pestañas) →
// ingreso / selección de empresa / shell.
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ErrorApi } from './api/cliente';
import { Bodega } from './bodega/Bodega';
import { Ruta } from './ruta/Ruta';
import { PantallaIngreso, ReingresoSesion, SelectorEmpresa } from './sesion/Login';
import { ProveedorSesion, useSesion } from './sesion/SesionContexto';
import { ProveedorPestanas, Shell } from './shell/Shell';
import { Vendedor } from './vendedor/Vendedor';

export function crearClienteConsultas(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Un 4xx es una respuesta, no una falla de red: no se reintenta. La red, una vez.
        retry: (n, e) => !(e instanceof ErrorApi && e.status > 0 && e.status < 500) && n < 1,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

function useEnHash(prefijo: string) {
  const [en, setEn] = useState(() => window.location.hash.startsWith(prefijo));
  useEffect(() => {
    const ver = () => setEn(window.location.hash.startsWith(prefijo));
    window.addEventListener('hashchange', ver);
    return () => window.removeEventListener('hashchange', ver);
  }, [prefijo]);
  return en;
}

function Raiz() {
  const s = useSesion();
  // Las apps instaladas viven en su carpeta (public/pwa-app.js): ahí abren aunque se pierda el «#».
  const enBodega = useEnHash('#/bodega') || window.location.pathname.startsWith('/app/bodega/');
  // App del peoneta: «MADECI Reparto» en /app/reparto/ (2026-10-28); /app/ruta/ es la del vendedor.
  const enRuta = useEnHash('#/ruta') || window.location.pathname.startsWith('/app/reparto/');
  const enVendedor = useEnHash('#/vendedor') || window.location.pathname.startsWith('/app/ruta/');
  // Vendedor en ruta (2026-10-25): sin señal la app abre igual con lo guardado en el celular.
  if (enVendedor && s.sinConexion) return <Vendedor sinConexion />;
  // Peoneta: igual, con la última lista de sus ODD y lo que tenga por enviar.
  if (enRuta && s.sinConexion) return <Ruta sinConexion />;
  if (s.estado === 'verificando')
    return (
      <main className="pantalla-ingreso">
        <p className="apagado">Verificando sesión…</p>
      </main>
    );
  if (s.estado === 'anonimo') return <PantallaIngreso />;
  if (!s.empresa) return <SelectorEmpresa />;
  // App del peoneta (docs/111, 2026-10-25): #/ruta, o directo si el usuario solo rinde rutas.
  const soloRuta =
    s.permisosCargados && s.puede('logistica.ruta.operar') && !s.puede('ventas.nota.ver');
  if (enRuta || soloRuta)
    return (
      <>
        <Ruta />
        {s.expirada && <ReingresoSesion />}
      </>
    );
  // Bodega en el celular (2026-10-25): #/bodega, o directo si el usuario solo opera la bodega.
  const soloBodega =
    s.permisosCargados && s.puede('logistica.od.recibir') && !s.puede('ventas.nota.ver');
  // Vendedor en ruta: #/vendedor, o directo si el usuario solo sale a vender (sin caja ni supervisión).
  const soloVendedor =
    s.permisosCargados &&
    s.puede('ventas.ruta.usar') &&
    !s.puede('caja.turno.operar') &&
    !s.puede('ventas.nota.cambiar_vendedor');
  if (enVendedor || soloVendedor)
    return (
      <>
        <Vendedor />
        {s.expirada && <ReingresoSesion />}
      </>
    );
  if (enBodega || soloBodega)
    return (
      <>
        <Bodega />
        {s.expirada && <ReingresoSesion />}
      </>
    );
  return (
    <>
      <Shell />
      {s.expirada && <ReingresoSesion />}
    </>
  );
}

export function App({ cliente }: { cliente?: QueryClient }) {
  const [qc] = useState(() => cliente ?? crearClienteConsultas());
  return (
    <QueryClientProvider client={qc}>
      <ProveedorPestanas>
        {(reiniciar) => (
          <ProveedorSesion alCambiarEmpresa={reiniciar}>
            <Raiz />
          </ProveedorSesion>
        )}
      </ProveedorPestanas>
    </QueryClientProvider>
  );
}
